import React, { useState, useEffect, useMemo, useCallback, FC } from 'react';
import AppLayout from 'layouts/app-layout';
import {
  DocumentData,
  getFileExtension,
  normalizeVerificationDateConfigs,
  validateNoDuplicatedDocumentData,
  validateRequiredDocumentData,
  validateVerificationDateDocumentData,
} from 'helpers/documentHelper';
import { Wrapper, FormHeader } from 'form/form.styles';
import {
  Abbreviation,
  Accordion,
  Button,
  FormControl,
  FieldSelect,
  FieldRadio,
  FieldCheckbox,
  Loading,
  PageTitle,
  SkeletonText,
} from 'components/atoms';
import { useHistory, useLocation } from 'react-router-dom';

import { useAuth } from 'auth';
import { useConfig } from 'config';

import {
  DueDiligence,
  DueDiligenceForm,
  DueDiligenceType,
  NoteContainer,
  AcknowledgeWrapper,
  FieldRadioContainer,
  SideModulesWrapper,
  ResponsiveContainer,
  LoadingMessage,
  GlobalSearch,
  ComboBoxContainer,
} from './../InitiateDueDiligence.styles';
import { useForm, useWatch, Controller, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { GatewayPermissions } from 'auth/constants/permissions';
import getSymbolFromCurrency from 'currency-symbol-map';
import { formatCurrencyAmount } from 'helpers/currencyHelper';

import { Breadcrumbs, Panel } from 'components/molecules';
import ThirdPartyOverviewSideModule from 'components/organisms/third-party-overview-side-module/ThirdPartyOverviewSideModule';
import { OtherInformation } from 'components/organisms';
import MultipleDocumentUploadWrapper from 'components/organisms/multiple-document-upload-wrapper';
import { LinkedThirdPartyFieldsConfigs, ThirdPartyModelResponse, ThirdPartyType } from 'types';
import { useFetch } from 'hooks';
import {
  Section,
  ThirdPartyInfo,
} from 'components/organisms/due-diligence-confirmation-modal/types';
import PotentialDuplicateModal from 'components/organisms/potential-duplicate-modal';
import {
  DuplicatedScreening,
  PotentialDuplicateModalTypes,
} from 'components/organisms/potential-duplicate-modal/types';
import {
  CheckDuplicateResponse,
  DocumentCategory,
  DocumentDataForMDDRequest,
  DraftEntityRetrievedFiles,
  DraftData,
  DueDiligenceTypesResponse,
  LinkedThirdParty,
  ReportTemplate,
  SelectedThirdParty,
} from '../types';
import { useUserSettings, useTerminologyConfig } from 'context/DataContext';
import ThirdPartiesSideModule from '../side-modules/ThirdPartiesSideModule';
import LinkageThirdPartiesForm from 'form/linkage-thirdParties/LinkageThirdPartiesForm';
import { getThirdPartyConfiguration } from 'services/getThirdPartyConfiguration';
import CbiInterviewInvitation from 'form/cbiInterviewInvitation/CbiInterviewInvitation';
import { BreadcrumbItem } from 'components/molecules/breadcrumbs/types';

interface InitiateDueDiligenceFormProps {
  loading: boolean;
  businessCategoryData: DocumentCategory[];
  individualCategoryData: DocumentCategory[];
  ddTypesData: DueDiligenceTypesResponse;
  thirdPartyData: ThirdPartyModelResponse;
  draftData: DraftData;
  optionalAddonsData: any;
  onScreeningSubmit: (data: any) => void;
  onDueDiligenceSubmit: (data: any) => void;
  onSaveDraft: (data: any) => void;
  loadingThirdParty?: boolean;
}

export interface InitiateDueDiligenceFormFields {
  linkedThirdParties: string[];
  documentsData: any;
  dueDiligenceType: string;
  screeningType: string;
  screeningSettings: string;
  monitoring: string;
  monitoringSettings: string;
  monitoringScreeningSettings: string;
  comments: string;
  customField1: string;
  customField2: string;
  customField3: string;
  reportCustomField1: string;
  reportCustomField2: string;
  reportCustomField3: string;
  reportTemplateId?: string;
  acknowledge: boolean;
  addOns: any[];
  expedite: boolean;
  hasAcknowledgedMessage: boolean;
  isGlobalSearch: boolean;
  applicantEmailAddress: string;
  applicantEmailAddressConfirmation: string;
}

const InitiateDueDiligenceForm: FC<InitiateDueDiligenceFormProps> = ({
  loading,
  businessCategoryData,
  individualCategoryData,
  ddTypesData,
  thirdPartyData,
  draftData,
  optionalAddonsData,
  onScreeningSubmit: onExternalScreeningSubmit,
  onDueDiligenceSubmit: onExternalDueDiligenceSubmit,
  onSaveDraft,
  loadingThirdParty,
}) => {
  //#region dependencies

  //#region props
  const {
    id,
    name: thirdPartyName,
    type: thirdPartyType,
    internalId: thirdPartyInternalId,
  } = thirdPartyData;

  const categories = [...individualCategoryData, ...businessCategoryData];
  const defaultOptionalCategoryId = (
    thirdPartyType === ThirdPartyType.Business ? businessCategoryData : individualCategoryData
  ).find((category) => category.category?.toLowerCase() === 'other')?.id;

  //#endregion props

  //#region context

  const { tokenInfo } = useAuth();
  const { hasPermission, isEnhancedDocumentUploadControlsEnabled } = useUserSettings();
  const terminologyConfig = useTerminologyConfig();
  const { msalSubscriptionKey, xFunctionsKey, vantageWebApi } = useConfig();

  const history = useHistory();

  const { search } = useLocation();

  const headers = useMemo(
    () => ({
      'Ocp-Apim-Subscription-Key': msalSubscriptionKey,
      'X-functions-Key': xFunctionsKey,
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
    }),
    [msalSubscriptionKey, xFunctionsKey, tokenInfo.accessToken]
  );
  //#endregion context

  //#region API calls
  const {
    data: relatedThirdPartiesRawData,
    trigger: fetchRelatedThirdPartiesLazy,
    loading: relatedThirdPartiesRawDataLoading,
  } = useFetch(`${vantageWebApi}/thirdParty/${id}/user/linkedThirdParties`, {
    method: 'GET',
    lazy: true,
    headers: {
      'Content-Type': 'application/json',
      Authorization: `Bearer ${tokenInfo.accessToken}`,
      'Ocp-Apim-Subscription-Key': `${msalSubscriptionKey}`,
    },
  });

  //#endregion API calls

  //#region use form
  const schema = yup.object().shape({
    currentStep: yup.number(),
    linkedThirdParties: yup.array(),
    screeningSettings: yup.mixed().test('screening-setting', 'Please select one!', (value) => {
      if (dueDiligenceType === 'Due Diligence') {
        return true;
      }
      return value !== 'DEFAULT';
    }),
    documentsData: yup
      .array()
      .of(
        yup.object().shape({
          name: yup.string().required(),
          size: yup.number().required(),
          type: yup.string().required(),
          category: yup.string().required(),
          categoryId: yup.number().required(),
          entityId: yup.string().required(),
          thirdPartyId: yup.string().nullable(),
          verificationDate: yup.string().nullable().optional(),
          // Backend
          id: yup.string().required(),
          fileDownloadName: yup.string().required(),
          uploadedBy: yup.string().required(),
          uploadedDate: yup.string().required(),
          documentSensitivity: yup.boolean().required(),
          expiryDate: yup.string().nullable().optional(),
          isOptionalDocument: yup.boolean(),
          isLinkedThirdPartyDocument: yup.boolean(),
        })
      )
      .test((documents: DocumentData[], context) => {
        if (dueDiligenceType !== 'Due Diligence') {
          return true;
        }
        return validateRequiredDocumentData(
          `Please attach all mandatory documents for the Primary subject as well as any linked ${terminologyConfig?.thirdPartyPluralNaming} included in the order`,
          documents,
          selectedReport?.documentConfig,
          thirdPartyData.type as ThirdPartyType,
          selectedRelatedThirdParties,
          context.createError,
          showMandatoryDocuments
        );
      })
      .test((documents: DocumentData[], context) => {
        if (dueDiligenceType !== 'Due Diligence') {
          return true;
        }
        return validateNoDuplicatedDocumentData(
          'You have two or more files uploaded with the same name, please remove duplication',
          documents,
          context.createError
        );
      })
      .test((documents: DocumentData[], context) => {
        if (dueDiligenceType !== 'Due Diligence') {
          return true;
        }
        return validateVerificationDateDocumentData(
          'You have one or more files uploaded with a invalid date, please update them',
          documents,
          selectedReport?.documentConfig,
          context.createError
        );
      }),
    monitoring: yup.mixed().test('screening-setting', 'Please select one!', (value) => {
      if (
        (screeningType === 'Self Service' || screeningType === 'Managed Service') &&
        isSelfServiceMonitoringEnabled
      ) {
        return value !== 'DEFAULT';
      }

      return true;
    }),
    comments: yup.string().max(2000, 'Maximum 2000 characters'),
    monitoringScreeningSettings: yup
      .mixed()
      .test('screening-setting', 'Please select one!', function (value) {
        if (
          this.parent.monitoring === 'enabled' &&
          (screeningType === 'Self Service' || screeningType === 'Managed Service')
        ) {
          return value !== 'DEFAULT';
        } else {
          return true;
        }
      }),
    customField1: yup
      .string()
      .max(80, 'Maximum 80 characters')
      .test('is-required', 'This is a required field', (value) => {
        if (dueDiligenceType === 'Screening') {
          return true;
        }
        if (dueDiligenceLevel?.customFieldsConfig?.customField1?.isMandatory && !value) {
          return false;
        }
        return true;
      }),
    customField2: yup
      .string()
      .max(80, 'Maximum 80 characters')
      .test('is-required', 'This is a required field', (value) => {
        if (dueDiligenceType === 'Screening') {
          return true;
        }
        if (dueDiligenceLevel?.customFieldsConfig?.customField2?.isMandatory && !value) {
          return false;
        }
        return true;
      }),
    customField3: yup
      .string()
      .max(80, 'Maximum 80 characters')
      .test('is-required', 'This is a required field', (value) => {
        if (dueDiligenceType === 'Screening') {
          return true;
        }
        if (dueDiligenceLevel?.customFieldsConfig?.customField3?.isMandatory && !value) {
          return false;
        }
        return true;
      }),
    reportCustomField1: yup
      .string()
      .max(80, 'Maximum 80 characters')
      .test('is-required', 'This is a required field', (value) => {
        if (dueDiligenceType === 'Screening') {
          return true;
        }
        if (selectedReport?.customFieldsConfig?.customField1?.isMandatory && !value) {
          return false;
        }
        return true;
      }),
    reportCustomField2: yup
      .string()
      .max(80, 'Maximum 80 characters')
      .test('is-required', 'This is a required field', (value) => {
        if (dueDiligenceType === 'Screening') {
          return true;
        }
        if (selectedReport?.customFieldsConfig?.customField2?.isMandatory && !value) {
          return false;
        }
        return true;
      }),
    reportCustomField3: yup
      .string()
      .max(80, 'Maximum 80 characters')
      .test('is-required', 'This is a required field', (value) => {
        if (dueDiligenceType === 'Screening') {
          return true;
        }
        if (selectedReport?.customFieldsConfig?.customField3?.isMandatory && !value) {
          return false;
        }
        return true;
      }),
    applicantEmailAddress: yup
      .string()
      .email('Invalid format. This must be an email address')
      .when([], {
        is: () => isManagedDueDiligence && thirdPartyData.isClientCitizenshipByInvestmentEnabled,
        then: yup.string().required('This is a required field'),
        otherwise: yup.string().notRequired(),
      }),
    applicantEmailAddressConfirmation: yup.string().when([], {
      is: () => isManagedDueDiligence && thirdPartyData.isClientCitizenshipByInvestmentEnabled,
      then: yup
        .string()
        .oneOf([yup.ref('applicantEmailAddress')], 'Emails do not match')
        .required('This is a required field'),
      otherwise: yup.string().notRequired(),
    }),
  });

  const dueDiligence = ddTypesData.find((x) => x.name === 'Due Diligence')?.dueDiligenceTypes[0];

  const dueDiligenceLevel = dueDiligence?.managedServiceDueDiligenceConfigs;

  //   //------- ( Draft Documents ) --------//

  //   //------- ( End Draft Documents ) --------//

  const reportTemplate = dueDiligenceLevel?.reportTemplates?.find(
    (r) => r.id.toString() === draftData?.requestDetails?.dueDiligenceTemplateId.toString()
  );

  const hasReportTemplate1 =
    reportTemplate?.customFieldsConfig?.customField1?.isEnabled &&
    draftData?.requestDetails?.reportCustomField1;
  const hasReportTemplate2 =
    reportTemplate?.customFieldsConfig?.customField2?.isEnabled &&
    draftData?.requestDetails?.reportCustomField2;
  const hasReportTemplate3 =
    reportTemplate?.customFieldsConfig?.customField3?.isEnabled &&
    draftData?.requestDetails?.reportCustomField3;
  const canExpedite = reportTemplate?.canExpedite && draftData?.requestDetails?.expedite;

  type DraftDocumentData = DocumentData & {
    thirdPartyId?: string | number;
    isLinkedThirdParty?: boolean;
    isRelatedEntity?: boolean;
    extension?: string;
  };

  type DraftDocumentGroup = DocumentDataForMDDRequest & {
    id?: string | number;
    isRelatedEntity?: boolean;
    documentsIds?: string[];
  };

  type DraftDocumentMetadata = {
    entityId?: string;
    isLinkedThirdPartyDocument: boolean;
    isOptionalDocument?: boolean;
  };

  const getDraftDocumentMetadataById = (draftData: DraftData) => {
    const metadataById = new Map<string, DraftDocumentMetadata>();

    (draftData.requestDetails?.documentsData ?? []).forEach((documentGroup: DraftDocumentGroup) => {
      const entityId = (documentGroup.thirdPartyId ?? documentGroup.id)?.toString();
      const isLinkedThirdPartyDocument =
        documentGroup.isLinkedThirdParty ?? documentGroup.isRelatedEntity ?? false;

      documentGroup.requiredDocumentsIds?.forEach((documentId) => {
        metadataById.set(documentId.toString(), {
          entityId,
          isLinkedThirdPartyDocument,
          isOptionalDocument: false,
        });
      });

      documentGroup.optionalDocumentsIds?.forEach((documentId) => {
        metadataById.set(documentId.toString(), {
          entityId,
          isLinkedThirdPartyDocument,
          isOptionalDocument: true,
        });
      });

      documentGroup.documentsIds?.forEach((documentId) => {
        if (!metadataById.has(documentId.toString())) {
          metadataById.set(documentId.toString(), {
            entityId,
            isLinkedThirdPartyDocument,
          });
        }
      });
    });

    return metadataById;
  };

  const normalizeDraftDocumentData = (
    document: DraftDocumentData,
    fallbackEntityId: string,
    fallbackIsLinkedThirdPartyDocument: boolean,
    metadataById: Map<string, DraftDocumentMetadata>
  ): DocumentData => {
    const documentId = document.id?.toString();
    const documentMetadata = documentId ? metadataById.get(documentId) : undefined;
    const fileExtension = getFileExtension(document.fileDownloadName) || document.extension;

    return {
      ...document,
      id: documentId,
      type: fileExtension?.replace('.', '') ?? document.type,
      entityId: document.entityId ?? documentMetadata?.entityId ?? fallbackEntityId,
      mainThirdPartyId: document.mainThirdPartyId ?? document.thirdPartyId?.toString(),
      verificationDate: document.verificationDate ?? null,
      documentSensitivity: document.documentSensitivity ?? false,
      expiryDate: document.expiryDate ?? null,
      isOptionalDocument:
        document.isOptionalDocument ?? documentMetadata?.isOptionalDocument ?? false,
      isLinkedThirdPartyDocument:
        document.isLinkedThirdPartyDocument ??
        documentMetadata?.isLinkedThirdPartyDocument ??
        fallbackIsLinkedThirdPartyDocument,
      fileStatus: document.fileStatus ?? null,
    };
  };

  const normalizeDraftDocumentsData = (draftData: DraftData): DocumentData[] => {
    const metadataById = getDraftDocumentMetadataById(draftData);

    if (draftData.documentsDetails?.length > 0) {
      return draftData.documentsDetails.map((document: DraftDocumentData) => {
        const entityId = (document.thirdPartyId ?? document.mainThirdPartyId ?? id).toString();
        const isLinkedThirdPartyDocument =
          document.isLinkedThirdParty ?? document.isRelatedEntity ?? false;

        return normalizeDraftDocumentData(
          document,
          entityId,
          isLinkedThirdPartyDocument,
          metadataById
        );
      });
    }

    return (draftData.entitiesRetrievedFiles ?? []).flatMap((entity: DraftEntityRetrievedFiles) =>
      (entity.filesInformation ?? []).map((document: DraftDocumentData) =>
        normalizeDraftDocumentData(
          document,
          entity.id.toString(),
          entity.isRelatedEntity,
          metadataById
        )
      )
    );
  };

  const formMethods = useForm<InitiateDueDiligenceFormFields>({
    mode: 'all',
    resolver: yupResolver(schema),
    defaultValues: {
      linkedThirdParties: [],
      documentsData: [],
      dueDiligenceType: ddTypesData.length === 1 ? 'Screening' : 'DEFAULT',
      screeningType: 'DEFAULT',
      screeningSettings: 'DEFAULT',
      monitoring: 'DEFAULT',
      monitoringSettings: 'DEFAULT',
      monitoringScreeningSettings: 'DEFAULT',
      comments: '',
      customField1: '',
      customField2: '',
      customField3: '',
      reportCustomField1: '',
      reportCustomField2: '',
      reportCustomField3: '',
      reportTemplateId: null,
      acknowledge: false,
      addOns: [],
      expedite: false,
      hasAcknowledgedMessage: false,
      isGlobalSearch: false,
      applicantEmailAddress: '',
      applicantEmailAddressConfirmation: '',
    },
  });

  const {
    register,
    handleSubmit,
    control,
    setValue,
    getValues,
    clearErrors,
    trigger,
    watch,
    reset,
    formState: { errors: formValidationErrors, isValid },
  } = formMethods;

  const linkedThirdParties = useWatch({ control, name: 'linkedThirdParties' });
  const [formattedLinkedThirdParties, setFormattedLinkedThirdParties] = useState<
    SelectedThirdParty[]
  >([]);

  const linkedThirdPartyConfigurations: LinkedThirdPartyFieldsConfigs = {
    isIndividualRelationshipTypeMandatory: thirdPartyData.individualRelationshipType,
    isIndividualOwnershipPercentageMandatory: thirdPartyData.individualOwnershipPercentage,
    isIndividualOwnershipPercentageEnabled: thirdPartyData.individualOwnershipPercentageEnabled,
    isCompanyRelationshipTypeMandatory: thirdPartyData.companyRelationshipType,
    isCompanyOwnershipPercentageMandatory: thirdPartyData.companyOwnershipPercentage,
    isCompanyOwnershipPercentageEnabled: thirdPartyData.companyOwnershipPercentageEnabled,
    isUnknownRelationshipTypeMandatory: thirdPartyData.unknownRelationshipType,
    isUnknownOwnershipPercentageMandatory: thirdPartyData.unknownOwnershipPercentage,
    isUnknownOwnershipPercentageEnabled: thirdPartyData.unknownOwnershipPercentageEnabled,
  };

  const documentsData = useWatch({ control, name: 'documentsData' });

  const dueDiligenceType = useWatch({
    control,
    name: 'dueDiligenceType',
  });
  const screeningType = useWatch({
    control,
    name: 'screeningType',
  });
  const reportTemplateId = useWatch({
    control,
    name: 'reportTemplateId',
  });

  const acknowledge = useWatch({
    control,
    name: 'acknowledge',
  });

  const hasAcknowledgedMessage = useWatch({
    control,
    name: 'hasAcknowledgedMessage',
  });

  const monitoringValue = useWatch({ control, name: 'monitoring' });

  const isManagedDueDiligence = dueDiligenceType === 'Due Diligence';
  const isScreeningDueDiligence = dueDiligenceType === 'Screening';
  const showMandatoryDocuments = isEnhancedDocumentUploadControlsEnabled !== false;

  //#endregion use form

  //#region internal states

  const [isOpenSection1, setIsOpenSection1] = useState(true);
  const [isOpenSection2, setIsOpenSection2] = useState(false);
  const [isOpenSection3, setIsOpenSection3] = useState(false);
  const [isOpenSection4, setIsOpenSection4] = useState(false);
  const [isOpenSection5, setIsOpenSection5] = useState(false);
  const [completedSections, setCompletedSections] = useState([]);
  const [pendingDocumentUploadsCount, setPendingDocumentUploadsCount] = useState(0);
  const [blockingDocumentUploadsCount, setBlockingDocumentUploadsCount] = useState(0);
  const [potentialDuplicateModalState, setPotentialDuplicateModalState] = useState<
    PotentialDuplicateModalTypes & { loading: boolean }
  >({
    isOpen: false,
    loading: false,
    clientScreenings: [],
    companyScreenings: [],
    canViewReport: true,
  });

  const [thirdPartyDataConfig, setThirdPartyDataConfig] = useState(null);

  const handlePendingDocumentUploadsChange = useCallback((pendingUploadsCount: number) => {
    setPendingDocumentUploadsCount((currentCount) =>
      Math.max(currentCount + pendingUploadsCount, 0)
    );
  }, []);

  const handleBlockingDocumentUploadsCountChange = useCallback((blockingUploadsCount: number) => {
    setBlockingDocumentUploadsCount(blockingUploadsCount);
  }, []);

  //#endregion internal states

  const initializeDraftStep = (currentStep) => {
    const completedSectionsArray = [];

    for (let i = 1; i < currentStep; i++) {
      completedSectionsArray.push(i);
    }

    setCompletedSections(completedSectionsArray);

    setIsOpenSection1(currentStep === 1);
    setIsOpenSection2(currentStep === 2);
    setIsOpenSection3(currentStep === 3);
    setIsOpenSection4(currentStep === 4);
    setIsOpenSection5(currentStep === 5);
  };

  //#region computed variables

  const urlSearchParams = new URLSearchParams(search);
  const draftIdParam = urlSearchParams ? urlSearchParams.get('draftId') : null;

  const screeningLevel = ddTypesData.find((x) => x.name === 'Screening')?.dueDiligenceTypes;

  const isSelfServiceMonitoringEnabledByDefault =
    screeningLevel &&
    screeningLevel.find(({ name }) => name === 'Self Service')
      ?.isMonitoringEnabledByDefaultOnNewCases;

  const isSelfServiceMonitoringEnabled =
    screeningLevel &&
    screeningLevel.find(({ name }) => name === 'Self Service')?.isMonitoringEnabled;

  const isManagedServiceMonitoringEnabledByDefault =
    screeningLevel &&
    screeningLevel.find(({ name }) => name === 'Managed Service')
      ?.isMonitoringEnabledByDefaultOnNewCases;

  const isManagedServiceMonitoringEnabled =
    screeningLevel &&
    screeningLevel.find(({ name }) => name === 'Managed Service')?.isMonitoringEnabled;

  const initialScreeningSettings =
    screeningLevel && Array.isArray(screeningLevel)
      ? screeningLevel
          .find(({ name }) => name === screeningType)
          ?.screeningSearchTypes?.map(({ id, description }) => ({
            name: description,
            value: id,
          }))
      : [];

  const isGlobalSearchEnabled = useMemo(() => {
    let hasGlobalSearchEnabled;
    if (screeningType === 'Self Service') {
      hasGlobalSearchEnabled = screeningLevel?.find(
        (x) => x.name === 'Self Service'
      )?.globalSearchEnabled;
    } else {
      hasGlobalSearchEnabled = screeningLevel?.find(
        (x) => x.name === 'Managed Service'
      )?.globalSearchEnabled;
    }

    return hasGlobalSearchEnabled;
  }, [screeningLevel, screeningType]);

  const monitoringScreeningSettings = useMemo(() => {
    return screeningLevel && Array.isArray(screeningLevel)
      ? screeningLevel
          .find(({ name }) => name === screeningType)
          ?.ongoingScreeningSearchTypes?.map(({ id, description }) => ({
            name: description,
            value: id,
          }))
      : [];
  }, [screeningType, screeningLevel]);

  const dueDiligenceTypes = useMemo(() => {
    if (screeningLevel) {
      return screeningLevel?.reduce(
        (acc, value) => {
          if (value.name === 'Self Service') {
            return {
              ...acc,
              selfService: true,
            };
          }
          if (value.name === 'Managed Service') {
            return {
              ...acc,
              managedService: true,
            };
          }
          return acc;
        },
        { selfService: false, managedService: false }
      );
    }
    return null;
  }, [screeningLevel]);

  const selectedReport: ReportTemplate | null = useMemo(() => {
    if (dueDiligenceLevel?.reportTemplates && reportTemplateId) {
      const template = dueDiligenceLevel.reportTemplates.find(
        (r) => r.id.toString() === reportTemplateId.toString()
      );

      if (template.documentConfig) {
        template.documentConfig = normalizeVerificationDateConfigs(template.documentConfig);
      }

      return template;
    }

    return null;
  }, [dueDiligenceLevel, reportTemplateId]);

  const permittedIndividualRelatedThirdParties =
    thirdPartyType === ThirdPartyType.Individual
      ? selectedReport?.maxIndividualRelatedThirdPartiesForIndividualThirdParty
      : selectedReport?.maxIndividualRelatedThirdPartiesForBusinessThirdParty;

  const permittedCompanyRelatedThirdParties =
    thirdPartyType === ThirdPartyType.Individual
      ? selectedReport?.maxBusinessRelatedThirdPartiesForIndividualThirdParty
      : selectedReport?.maxBusinessRelatedThirdPartiesForBusinessThirdParty;

  const hasPermittedRelatedThirdParties: number =
    permittedIndividualRelatedThirdParties + permittedCompanyRelatedThirdParties;

  const selectedRelatedThirdParties: SelectedThirdParty[] = useMemo(() => {
    const linkedThirdPartiesState = getValues('linkedThirdParties');
    try {
      return (
        formattedLinkedThirdParties?.filter((re) => {
          return linkedThirdPartiesState.includes(String(re.id));
        }) ?? []
      );
    } catch (error) {
      return [];
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [linkedThirdParties, formattedLinkedThirdParties]);

  const isEntityTypeUnknown = thirdPartyData.type === ThirdPartyType.Unknown;

  const isAcknowledgeVisible = (): boolean =>
    hasPermittedRelatedThirdParties > 0 &&
    (selectedRelatedThirdParties.filter((re) => re.type == ThirdPartyType.Individual).length >
      permittedIndividualRelatedThirdParties ||
      selectedRelatedThirdParties.filter((re) => re.type == ThirdPartyType.Business).length >
        permittedCompanyRelatedThirdParties);

  const isAcknowledgeMessageVisible = useMemo(() => {
    return selectedReport && selectedReport?.includeAcknowledgementMessage;
  }, [selectedReport]);

  const permittedDocumentsUploadAndPermissions = useMemo(() => {
    if (!hasPermission(GatewayPermissions.UploadDocuments)) {
      return false;
    }
    if (!selectedReport?.documentConfig) {
      return false;
    }
    let shouldRender = false;
    if (
      showMandatoryDocuments &&
      selectedReport?.documentConfig?.isRequiredDocumentsForThirdParty
    ) {
      const tpDocs = selectedReport?.documentConfig?.thirdPartyDocuments;
      if (
        tpDocs?.thirdPartyConfiguredDocumentsIndividual.length > 0 &&
        thirdPartyType == ThirdPartyType.Individual
      ) {
        shouldRender = true;
      }
      if (
        tpDocs?.thirdPartyConfiguredDocumentsBusiness.length > 0 &&
        thirdPartyType == ThirdPartyType.Business
      ) {
        shouldRender = true;
      }
    }
    if (
      showMandatoryDocuments &&
      selectedReport?.documentConfig?.isRequiredDocumentsForRelatedThirdParties
    ) {
      const reDocs = selectedReport?.documentConfig?.relatedThirdPartiesDocuments;
      if (
        reDocs?.linkedThirdPartyConfiguredDocumentsIndividual.length > 0 &&
        selectedRelatedThirdParties.find((re) => re.type == ThirdPartyType.Individual)
      ) {
        shouldRender = true;
      }
      if (
        reDocs?.linkedThirdPartyConfiguredDocumentsIndividual.length > 0 &&
        selectedRelatedThirdParties.find((re) => re.type == ThirdPartyType.Business)
      ) {
        shouldRender = true;
      }
    }
    if (selectedReport?.documentConfig?.isOptionalDocumentsForThirdParty) {
      shouldRender = true;
    }
    return shouldRender;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport, selectedRelatedThirdParties, thirdPartyType, showMandatoryDocuments]);

  //#endregion computed variables

  //#region validations

  const ddTypeIsVisible = (level) => {
    switch (level) {
      case 'Screening':
        return (
          hasPermission(GatewayPermissions.InitiateDueDiligenceSelfServiceScreening) ||
          hasPermission(GatewayPermissions.InitiateDueDiligenceManagedServiceScreening)
        );
      case 'Due Diligence':
        return hasPermission(GatewayPermissions.OrderManagedDueDiligence) && !isEntityTypeUnknown;
      case 'UBO':
        return hasPermission(GatewayPermissions.OrderOwnershipManagedDueDiligence);
      default:
        return true;
    }
  };

  useEffect(() => {
    if (!draftData) {
      return;
    }
    reset({
      linkedThirdParties:
        draftData.requestDetails.linkedThirdParties ??
        draftData.requestDetails.relatedEntities ??
        [],
      documentsData: normalizeDraftDocumentsData(draftData),
      dueDiligenceType: draftData.dueDiligenceTypeId === 5 ? 'Due Diligence' : 'DEFAULT',
      screeningType: 'DEFAULT',
      screeningSettings: 'DEFAULT',
      monitoring: 'DEFAULT',
      monitoringSettings: 'DEFAULT',
      monitoringScreeningSettings: 'DEFAULT',
      comments: draftData?.requestDetails?.comments,
      customField1:
        dueDiligenceLevel?.customFieldsConfig?.customField1?.isEnabled &&
        draftData?.requestDetails?.customField1
          ? draftData?.requestDetails?.customField1
          : '',
      customField2:
        dueDiligenceLevel?.customFieldsConfig?.customField2?.isEnabled &&
        draftData?.requestDetails?.customField2
          ? draftData?.requestDetails?.customField2
          : '',
      customField3:
        dueDiligenceLevel?.customFieldsConfig?.customField3?.isEnabled &&
        draftData?.requestDetails?.customField3
          ? draftData?.requestDetails?.customField3
          : '',
      reportCustomField1: hasReportTemplate1 ? draftData?.requestDetails?.reportCustomField1 : '',
      reportCustomField2: hasReportTemplate2 ? draftData?.requestDetails?.reportCustomField2 : '',
      reportCustomField3: hasReportTemplate3 ? draftData?.requestDetails?.reportCustomField3 : '',
      reportTemplateId: draftData?.requestDetails?.dueDiligenceTemplateId,
      acknowledge: false,
      addOns:
        reportTemplate?.optionalAddOns?.length > 0 && draftData?.requestDetails?.addOns?.length > 0
          ? draftData?.requestDetails?.addOns
          : [],
      expedite: canExpedite ? draftData?.requestDetails?.expedite : false,
      hasAcknowledgedMessage: false,
      isGlobalSearch: false,
      applicantEmailAddress: draftData.requestDetails.applicantEmailAddress,
    });
    initializeDraftStep(draftData.currentStep);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [draftData]);

  useEffect(() => {
    setValue('hasAcknowledgedMessage', false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedReport]);

  useEffect(() => {
    const queryParams: Record<string, string | boolean> = {};

    if (permittedCompanyRelatedThirdParties === 0) {
      queryParams.type = 'Individual';
    } else if (permittedIndividualRelatedThirdParties === 0) {
      queryParams.type = 'Business';
    }

    fetchRelatedThirdPartiesLazy({
      queryParams,
    });
  }, [
    fetchRelatedThirdPartiesLazy,
    id,
    permittedCompanyRelatedThirdParties,
    permittedIndividualRelatedThirdParties,
  ]);

  useEffect(() => {
    setValue('screeningSettings', 'DEFAULT');

    if (screeningType === 'Self Service') {
      if (!isSelfServiceMonitoringEnabled) {
        setValue('monitoring', 'disabled');
      }
      if (isSelfServiceMonitoringEnabledByDefault) {
        setValue('monitoring', 'enabled');
      }
    } else if (screeningType === 'Managed Service') {
      if (!isManagedServiceMonitoringEnabled) {
        setValue('monitoring', 'disabled');
      }
      if (isManagedServiceMonitoringEnabledByDefault) {
        setValue('monitoring', 'enabled');
      }
    } else {
      setValue('monitoring', 'DEFAULT');
    }

    setValue('monitoringScreeningSettings', 'DEFAULT');
    clearErrors('screeningSettings');
  }, [
    screeningType,
    setValue,
    clearErrors,
    isSelfServiceMonitoringEnabledByDefault,
    isManagedServiceMonitoringEnabledByDefault,
    isSelfServiceMonitoringEnabled,
    isManagedServiceMonitoringEnabled,
  ]);

  const checkDuplicatedMDDScreeningFetch = async (
    thirdPartyId: string,
    linkedThirdPartyIds: string[],
    templateId: string
  ) => {
    try {
      const url = new URL(
        `${vantageWebApi}/thirdParty/${thirdPartyId}/reportTemplate/${templateId}/duplicates`
      );

      linkedThirdPartyIds.forEach((linkedThirdPartyId) => {
        url.searchParams.append('linkedThirdPartyIds', linkedThirdPartyId.toString());
      });

      const response = await fetch(url.toString(), {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${tokenInfo.accessToken}`,
          'Ocp-Apim-Subscription-Key': `${msalSubscriptionKey}`,
          'X-functions-Key': xFunctionsKey,
        },
      });

      if (!response.ok) {
        throw new Error(response.statusText);
      }

      const jsonResponse = await response.json();

      return jsonResponse as CheckDuplicateResponse;
    } catch (error) {
      console.error('Error: fetch to /onlinescreening/duplicates', error);
    }
  };

  const evaluateIfShouldOpenDuplicatedMDDModal = async () => {
    try {
      setPotentialDuplicateModalState((prev) => ({
        ...prev,
        loading: true,
      }));
      const checkDuplicatedMDDScreening = await checkDuplicatedMDDScreeningFetch(
        id,
        selectedRelatedThirdParties.map((re) => re.id),
        selectedReport?.id
      );

      const { byClient, byCompany } = checkDuplicatedMDDScreening;

      const clientScreenings: DuplicatedScreening[] = byClient.map((screening) => ({
        id: screening.screeningId,
        createdDate: screening.requestDate,
        reportStatus: screening.reportStatus,
        userFullName: screening.requesterName,
      }));

      const companyScreenings: DuplicatedScreening[] = byCompany.map((screening) => ({
        id: screening.screeningId,
        createdDate: screening.requestDate,
        reportStatus: screening.reportStatus,
        userFullName: screening.requesterName,
      }));

      setPotentialDuplicateModalState((prev) => ({
        ...prev,
        loading: false,
      }));

      if (clientScreenings.length > 0 || companyScreenings.length > 0) {
        setPotentialDuplicateModalState({
          isOpen: true,
          loading: false,
          canViewReport: hasPermission(GatewayPermissions.Admin),
          clientScreenings,
          companyScreenings,
        });
      }
    } catch (error) {
      console.error('Error: Open Potential Duplicates Modal', error);
    } finally {
      setPotentialDuplicateModalState((prev) => ({
        ...prev,
        loading: false,
      }));
    }
  };

  const stepOneEnableNextSections = () => {
    setIsOpenSection1(false);
    const temp = [...completedSections];
    if (isScreeningDueDiligence || isManagedDueDiligence) {
      const index = temp.indexOf(1);
      if (hasPermittedRelatedThirdParties === 0) {
        if (!permittedDocumentsUploadAndPermissions) {
          setIsOpenSection4(true);
          if (index === -1) {
            setCompletedSections([1, 2, 3, ...temp]);
            return;
          }
        } else {
          setIsOpenSection3(true);
          if (index === -1) {
            setCompletedSections([1, 2, ...temp]);
            return;
          }
        }
      } else {
        setIsOpenSection2(true);
        if (index === -1) {
          setCompletedSections([1, ...temp]);
          return;
        }
      }
    }
  };

  const stepTwoEnableNextSections = () => {
    const temp = [...completedSections];
    if (!permittedDocumentsUploadAndPermissions) {
      setIsOpenSection2(false);
      setIsOpenSection3(false);
      setIsOpenSection4(true);
      const index = temp.indexOf(2);
      if (index === -1) {
        setCompletedSections([2, 3, ...completedSections]);
      }
    } else {
      setIsOpenSection2(false);
      setIsOpenSection3(true);
      const index = temp.indexOf(2);
      if (index === -1) {
        setCompletedSections([2, ...completedSections]);
      }
    }
  };

  const stepThreeEnableNextSections = () => {
    const temp = [...completedSections];
    setIsOpenSection3(false);
    setIsOpenSection4(true);
    const index = temp.indexOf(3);
    if (index === -1) {
      setCompletedSections([3, ...completedSections]);
    }
  };

  const stepFourEnableNextSections = () => {
    const temp = [...completedSections];
    setIsOpenSection4(false);
    setIsOpenSection5(true);
    const index = temp.indexOf(4);
    if (index === -1) {
      setCompletedSections([4, ...completedSections]);
    }
  };

  const handleComplianceCheckTypeNextSectionClick = async () => {
    if (hasPermittedRelatedThirdParties === 0) {
      await evaluateIfShouldOpenDuplicatedMDDModal();
    }
    stepOneEnableNextSections();
  };

  const handleRelatedEntitiesNextSectionClick = async () => {
    await evaluateIfShouldOpenDuplicatedMDDModal();
    stepTwoEnableNextSections();
  };

  const handleThirdPartyDocumentsNextSectionClick = async () => {
    if (!hasPermittedRelatedThirdParties) {
      await checkDuplicatedMDDScreeningFetch(
        id,
        selectedRelatedThirdParties.map((re) => re.id),
        selectedReport?.id
      );
    }

    await trigger('documentsData');
    if ((formValidationErrors.documentsData as any)?.message) {
      return;
    }

    stepThreeEnableNextSections();
  };

  const handleOtherInfoNextSectionClick = async () => {
    stepFourEnableNextSections();
  };

  const isDisabledRelatedEntitesNextSection = useMemo(() => {
    if (!acknowledge && isAcknowledgeVisible()) {
      return true;
    }
    if (potentialDuplicateModalState.loading) {
      return true;
    }

    return false;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    acknowledge,
    isAcknowledgeVisible,
    hasPermittedRelatedThirdParties,
    selectedRelatedThirdParties,
    permittedCompanyRelatedThirdParties,
    potentialDuplicateModalState.loading,
  ]);

  useEffect(() => {
    if (isSelfServiceMonitoringEnabledByDefault && screeningType === 'Self Service') {
      setValue('monitoring', 'enabled');
    }

    if (isManagedServiceMonitoringEnabledByDefault && screeningType === 'Managed Service') {
      setValue('monitoring', 'enabled');
    }

    if (
      dueDiligenceTypes?.selfService &&
      !isSelfServiceMonitoringEnabled &&
      !isSelfServiceMonitoringEnabledByDefault &&
      screeningType === 'Self Service'
    ) {
      setValue('monitoring', 'disabled');
    }

    if (
      dueDiligenceTypes?.managedService &&
      !isManagedServiceMonitoringEnabled &&
      !isManagedServiceMonitoringEnabledByDefault &&
      screeningType === 'Managed Service'
    ) {
      setValue('monitoring', 'disabled');
    }

    if (dueDiligenceType === 'Screening') {
      if (dueDiligenceTypes?.selfService && !dueDiligenceTypes?.managedService) {
        setValue('screeningType', 'Self Service');
      }
      if (!dueDiligenceTypes?.selfService && dueDiligenceTypes?.managedService) {
        setValue('screeningType', 'Managed Service');
      }
    }

    if (dueDiligenceType === 'Due Diligence' && dueDiligenceLevel?.reportTemplates?.length === 1) {
      setValue('reportTemplateId', dueDiligenceLevel.reportTemplates[0].id);
    }
  }, [
    screeningType,
    isSelfServiceMonitoringEnabled,
    isSelfServiceMonitoringEnabledByDefault,
    isManagedServiceMonitoringEnabled,
    isManagedServiceMonitoringEnabledByDefault,
    setValue,
    dueDiligenceTypes?.selfService,
    dueDiligenceTypes?.managedService,
    dueDiligenceType,
    dueDiligenceLevel?.reportTemplates,
  ]);

  useEffect(() => {
    if (
      monitoringScreeningSettings &&
      monitoringScreeningSettings.length === 1 &&
      (screeningType === 'Self Service' || screeningType === 'Managed Service')
    ) {
      setValue('monitoringScreeningSettings', monitoringScreeningSettings[0].value.toString());
      return;
    }

    setValue('monitoringScreeningSettings', 'DEFAULT');
  }, [setValue, monitoringValue, monitoringScreeningSettings, screeningType]);

  useEffect(() => {
    clearErrors('documentsData');
    if (acknowledge && selectedRelatedThirdParties?.length > hasPermittedRelatedThirdParties) {
      setValue('acknowledge', false);
    }
    // ignoring getPermittedRelatedEntities, to avoid warning.
    // eslint-disable-next-line
  }, [setValue, clearErrors, selectedRelatedThirdParties?.length]);

  useEffect(() => {
    if (!thirdPartyData) {
      return;
    }

    const { type } = thirdPartyData;
    getThirdPartyConfiguration(vantageWebApi, headers, type).then((config) =>
      setThirdPartyDataConfig(config)
    );
  }, [thirdPartyData, vantageWebApi, headers]);
  //#endregion validations

  //#region handlers

  const normalizeDocumentsDataForMDDRequest = (
    documentsData: DocumentData[]
  ): DocumentDataForMDDRequest[] => {
    return documentsData.reduce((normalized, currentDocumentData: DraftDocumentData) => {
      const documentId = currentDocumentData.id?.toString();
      const thirdPartyId = (
        currentDocumentData.entityId ??
        currentDocumentData.thirdPartyId ??
        currentDocumentData.mainThirdPartyId
      )?.toString();
      const isLinkedThirdParty =
        currentDocumentData.isLinkedThirdPartyDocument ??
        currentDocumentData.isLinkedThirdParty ??
        currentDocumentData.isRelatedEntity ??
        false;
      const isOptionalDocument = currentDocumentData.isOptionalDocument ?? false;

      if (!documentId || !thirdPartyId) {
        return normalized;
      }

      const found = normalized.find(
        (doc) => doc.thirdPartyId === thirdPartyId && doc.isLinkedThirdParty === isLinkedThirdParty
      );
      if (found) {
        if (isOptionalDocument) {
          found.optionalDocumentsIds.push(documentId);
        } else {
          found.requiredDocumentsIds.push(documentId);
        }

        return normalized;
      } else {
        const newRecord = {
          thirdPartyId,
          isLinkedThirdParty,
          requiredDocumentsIds: [],
          optionalDocumentsIds: [],
        };

        if (isOptionalDocument) {
          newRecord.optionalDocumentsIds = [documentId];
        } else {
          newRecord.requiredDocumentsIds = [documentId];
        }

        return [...normalized, newRecord];
      }
    }, [] as DocumentDataForMDDRequest[]);
  };

  const findCurrentStep = () => {
    const isOpenSections = [
      isOpenSection1,
      isOpenSection2,
      isOpenSection3,
      isOpenSection4,
      isOpenSection5,
    ];
    const openIndex = isOpenSections.reverse().findIndex((isOpen) => isOpen);
    return openIndex === -1 ? 1 : isOpenSections.length - openIndex;
  };

  const sectionDueDiligenceConfirmationModal = (
    formData: InitiateDueDiligenceFormFields,
    selectedReport: any,
    selectedRelatedEntities: any[],
    dueDiligenceLevelState: any,
    optionalAddonsData: any
  ): Section => {
    const blocks = [];

    if (selectedReport?.fee) {
      const fee = formatCurrencyAmount(selectedReport?.fee);
      const maxFee = selectedReport?.maxFee ? formatCurrencyAmount(selectedReport?.maxFee) : '';
      const currency = getSymbolFromCurrency(selectedReport?.feeCurrencyCode);
      const feeResult =
        selectedReport.isFeeRange && selectedReport.maxFee
          ? `${currency}${fee} - ${currency}${maxFee}`
          : `${currency}${fee}`;
      blocks.push({
        title: 'Fee',
        lines: [feeResult],
      });
    }

    if (selectedReport?.managedServiceDueDiligenceSLA) {
      blocks.push({
        title: 'Standard delivery timeframe',
        lines: [`Up to ${selectedReport?.managedServiceDueDiligenceSLA} working days`],
      });
    }

    const expediteFee = `${
      selectedReport?.expediteFee
        ? ' - ' +
          getSymbolFromCurrency(selectedReport?.expediteFeeCurrencyCode) +
          formatCurrencyAmount(selectedReport?.expediteFee)
        : ''
    }`;

    blocks.push({
      title: 'Expedited?',
      lines: [`${formData.expedite ? `Yes${expediteFee}` : 'No'}`],
    });

    if (selectedRelatedEntities.length > 0) {
      blocks.push({
        title: `Related ${terminologyConfig?.thirdPartyPluralNaming}`,
        lines: selectedRelatedEntities.map((relatedEntity) => relatedEntity?.name),
      });
    }

    if (formData?.comments) {
      blocks.push({
        title: 'Other information',
        lines: [formData?.comments],
      });
    }

    if (
      dueDiligenceLevelState?.customFieldsConfig?.customField1?.isEnabled &&
      formData?.customField1
    ) {
      blocks.push({
        title: dueDiligenceLevelState.customFieldsConfig?.customField1.label,
        lines: [formData?.customField1],
      });
    }

    if (
      dueDiligenceLevelState?.customFieldsConfig?.customField2?.isEnabled &&
      formData?.customField2
    ) {
      blocks.push({
        title: dueDiligenceLevelState.customFieldsConfig?.customField2.label,
        lines: [formData?.customField2],
      });
    }

    if (
      dueDiligenceLevelState?.customFieldsConfig?.customField3?.isEnabled &&
      formData?.customField3
    ) {
      blocks.push({
        title: dueDiligenceLevelState.customFieldsConfig?.customField3.label,
        lines: [formData?.customField3],
      });
    }

    if (
      selectedReport?.customFieldsConfig?.customField1?.isEnabled &&
      formData?.reportCustomField1
    ) {
      blocks.push({
        title: selectedReport.customFieldsConfig.customField1.label,
        lines: [formData?.reportCustomField1],
      });
    }

    if (
      selectedReport?.customFieldsConfig?.customField2?.isEnabled &&
      formData?.reportCustomField2
    ) {
      blocks.push({
        title: selectedReport.customFieldsConfig.customField2.label,
        lines: [formData?.reportCustomField2],
      });
    }

    if (
      selectedReport?.customFieldsConfig?.customField3?.isEnabled &&
      formData?.reportCustomField3
    ) {
      blocks.push({
        title: selectedReport.customFieldsConfig.customField3.label,
        lines: [formData?.reportCustomField3],
      });
    }

    if (formData?.addOns?.filter((ao) => ao).length > 0) {
      blocks.push({
        title: 'Add-Ons (and fees):',
        lines: [
          formData.addOns
            .map((addon) => {
              if (addon) {
                const selectedAddon = selectedReport?.optionalAddOns.find(
                  (oa) => oa.optionalAddOnId === Number(addon)
                );
                const addonObject = optionalAddonsData?.find(
                  (obj) => obj.id === selectedAddon.optionalAddOnId
                );
                const addonFee = selectedAddon.fee
                  ? ` - ${getSymbolFromCurrency(
                      selectedAddon.feeCurrencyCode
                    )}${formatCurrencyAmount(selectedAddon.fee)}`
                  : '';
                return addonObject ? `${addonObject?.description}${addonFee}` : '';
              } else {
                return '';
              }
            })
            .filter((oa) => oa),
        ],
      });
    }

    return {
      title: 'Additional Information',
      blocks,
    };
  };

  const getDueDiligenceData = () => {
    const normalizedDocs = normalizeDocumentsDataForMDDRequest(documentsData);
    return {
      currentStep: findCurrentStep(),
      dueDiligenceTypeId: dueDiligence?.id,
      dueDiligenceTemplateId: reportTemplateId,
      linkedThirdParties: getValues('linkedThirdParties'),
      comments: getValues('comments'),
      addOns: getValues('addOns')
        .filter((id) => id !== false)
        .map((id) => parseInt(id)),
      customField1: getValues('customField1'),
      customField2: getValues('customField2'),
      customField3: getValues('customField3'),
      reportCustomField1: getValues('reportCustomField1'),
      reportCustomField2: getValues('reportCustomField2'),
      reportCustomField3: getValues('reportCustomField3'),
      expedite: getValues('expedite'),
      documentsData: normalizedDocs,
      draftId: draftIdParam,
      applicantEmailAddress: getValues('applicantEmailAddress'),
      summaryDescriptions: {
        selectedReport,
        SelectedThirdParties: selectedRelatedThirdParties,
        dueDiligenceType,
        dueDiligenceLevel,
        hasPermittedRelatedEntities: hasPermittedRelatedThirdParties,
        screeningType,
      },

      thirdPartyInfo: {
        thirdPartyLabel: terminologyConfig?.thirdPartySingularNaming,
        name: thirdPartyName,
        internalId: thirdPartyInternalId,
        dueDiligenceType: dueDiligenceType,
        level: selectedReport?.reportName,
        attachments: normalizedDocs?.flatMap((entity) => [
          ...(entity.requiredDocumentsIds || []),
          ...(entity.optionalDocumentsIds || []),
        ])?.length,
      } as ThirdPartyInfo,
      section: sectionDueDiligenceConfirmationModal(
        getValues(),
        selectedReport,
        selectedRelatedThirdParties,
        dueDiligenceLevel,
        optionalAddonsData
      ) as Section,
    };
  };

  const getScreeningData = () => {
    return {
      screeningSearchTypeId: Number(getValues('screeningSettings')),
      isGlobalSearch: isGlobalSearchEnabled ? getValues('isGlobalSearch') : null,
      isMonitoringEnabled:
        screeningType === 'Self Service' || 'Managed Service'
          ? getValues('monitoring') === 'enabled'
          : false,
      ongoingScreeningSearchTypeId:
        getValues('monitoringScreeningSettings') === 'DEFAULT' ||
        getValues('monitoring') === 'disabled'
          ? null
          : Number(getValues('monitoringScreeningSettings')),
      summaryDescriptions: {
        selectedReport,
        SelectedThirdParties: selectedRelatedThirdParties,
        dueDiligenceType,
        dueDiligenceLevel,
        hasPermittedRelatedEntities: hasPermittedRelatedThirdParties,
        screeningType,
      },
    };
  };

  const formatThirdPartyEntity = (entity: Record<string, any>) =>
    ({
      gender: entity.gender || '',
      jobTitle: entity.linkType || '',
      primaryCountry: entity.primaryCountry,
      ...entity,
    } as SelectedThirdParty);

  useEffect(() => {
    if (relatedThirdPartiesRawData && Array.isArray(relatedThirdPartiesRawData)) {
      const formattedThirdParties = relatedThirdPartiesRawData.map((entity) =>
        formatThirdPartyEntity(entity)
      );
      setFormattedLinkedThirdParties(formattedThirdParties);
    }
  }, [relatedThirdPartiesRawData]);

  const prependLinkedThirdParty = (entity: LinkedThirdParty) => {
    setFormattedLinkedThirdParties((prev) => [...prev, formatThirdPartyEntity(entity)]);
    setValue('linkedThirdParties', [entity.id, ...getValues('linkedThirdParties')]);
  };

  const updateLinkedThirdParty = (entity: LinkedThirdParty) => {
    const updatedEntity = formatThirdPartyEntity(entity);

    setFormattedLinkedThirdParties((prevEntities) => {
      const entityIndex = prevEntities.findIndex(
        (item) => String(item.id) === String(updatedEntity.id)
      );
      if (entityIndex === -1) return prevEntities;
      const updatedEntities = [...prevEntities];
      updatedEntities[entityIndex] = updatedEntity;
      return updatedEntities;
    });

    setValue('linkedThirdParties', [updatedEntity.id, ...getValues('linkedThirdParties')]);
  };

  const handleScreeningSubmit = async () => {
    await handleSubmit(() => {
      onExternalScreeningSubmit(getScreeningData());
    })();
  };

  const handleDueDiligenceSubmit = async () => {
    trigger();
    const completedSection = thirdPartyData.isClientCitizenshipByInvestmentEnabled ? 4 : 3;
    setCompletedSections([completedSection, ...completedSections]);
    await handleSubmit(() => {
      onExternalDueDiligenceSubmit(getDueDiligenceData());
    })();
  };

  const handleSaveDraft = () => {
    onSaveDraft(getDueDiligenceData());
  };
  //#endregion handlers

  //#endregion dependencies

  //#region auxiliarComponents

  type SubmitButtonsTypes = {
    handleSubmit?: () => {};
    handleNextSection?: () => {};
    isLoadingNextButton?: boolean;
    showSaveDraftButton?: boolean;
    disableNextButton?: boolean;
  };

  const SubmitButtons = ({
    handleSubmit,
    handleNextSection = null,
    isLoadingNextButton = false,
    showSaveDraftButton = true,
    disableNextButton = false,
  }: SubmitButtonsTypes) => {
    const areDocumentRequestsPending = pendingDocumentUploadsCount > 0;
    const areDocumentsUploading = areDocumentRequestsPending || blockingDocumentUploadsCount > 0;

    return (
      <div className="buttons">
        <Button text type="button" onClick={() => history.push(`/third-party/profile/${id}`)}>
          Cancel
        </Button>
        {showSaveDraftButton && (
          <Button
            className="next-button"
            secondary
            outline
            type="button"
            onClick={handleSaveDraft}
            disabled={areDocumentRequestsPending}
          >
            Save Draft
          </Button>
        )}
        {handleNextSection === null ? (
          <>
            <Button
              className="submit-button"
              type="button"
              secondary
              onClick={handleSubmit}
              disabled={
                areDocumentsUploading ||
                (isManagedDueDiligence && !acknowledge && isAcknowledgeVisible()) ||
                (isManagedDueDiligence && !hasAcknowledgedMessage && isAcknowledgeMessageVisible)
              }
            >
              Submit order
            </Button>
            {areDocumentsUploading && (
              <p style={{ color: 'red', marginTop: '8px' }}>Documents are still uploading</p>
            )}
          </>
        ) : (
          <Button
            className="next-button"
            type="button"
            secondary
            onClick={handleNextSection}
            disabled={disableNextButton || areDocumentsUploading}
          >
            {isLoadingNextButton ? <Loading /> : 'Next section'}
          </Button>
        )}
      </div>
    );
  };

  //#endregion auxiliarComponents
  const getBreadcrumb = (): BreadcrumbItem[] => {
    return [
      {
        label: terminologyConfig?.thirdPartySingularNaming + ' manager',
        route: '/',
      },
      {
        label: thirdPartyData?.name,
        route: `/third-party/profile/${thirdPartyData?.id}`,
      },

      {
        label: `Initiate Compliance`,
      },
    ] as BreadcrumbItem[];
  };

  return (
    <AppLayout
      topBarSlot={loadingThirdParty ? <SkeletonText /> : <Breadcrumbs items={getBreadcrumb()} />}
    >
      <PageTitle title={`${terminologyConfig?.thirdPartySingularNaming}: ${thirdPartyName}`} />
      <FormProvider {...formMethods}>
        <DueDiligence>
          <Wrapper>
            <FormHeader>Initiate compliance check</FormHeader>
            {loading ? (
              <>
                <Loading />
                <LoadingMessage>Please wait. Your order is being processed.</LoadingMessage>
              </>
            ) : (
              <DueDiligenceForm>
                <Accordion
                  title="Compliance check type"
                  isOpen={isOpenSection1}
                  setIsOpen={() => {}}
                  onClick={(event) => {
                    event.preventDefault();
                    setIsOpenSection1(!isOpenSection1);
                  }}
                  completed={completedSections.includes(1)}
                >
                  <DueDiligenceType>
                    <ResponsiveContainer className="radio-group">
                      {ddTypesData.map(
                        (level, index) =>
                          ddTypeIsVisible(level.name) && (
                            <FieldRadio
                              key={index}
                              label={level.name}
                              id={level.name}
                              value={level.name}
                              name={`dueDiligenceType`}
                              register={register}
                            >
                              <Abbreviation abbreviation={level.name.replace(' ', '')} />
                              <span>{level.name}</span>
                            </FieldRadio>
                          )
                      )}
                    </ResponsiveContainer>
                  </DueDiligenceType>
                  {isScreeningDueDiligence && (
                    <FormControl helperText="*Mandatory Fields">
                      <label>Level</label>
                      <ResponsiveContainer className="radio-group">
                        {dueDiligenceTypes?.selfService &&
                          hasPermission(
                            GatewayPermissions.InitiateDueDiligenceSelfServiceScreening
                          ) && (
                            <FieldRadio
                              label="Self Service Screening"
                              id="selfServiceScreening"
                              value="Self Service"
                              name="screeningType"
                              register={register}
                              checked={!dueDiligenceTypes?.managedService}
                            >
                              <span>Self Service Screening</span>
                            </FieldRadio>
                          )}
                        {dueDiligenceTypes?.managedService &&
                          hasPermission(
                            GatewayPermissions.InitiateDueDiligenceManagedServiceScreening
                          ) && (
                            <FieldRadio
                              label="Managed Service Screening"
                              id="managedServiceScreening"
                              value="Managed Service"
                              name="screeningType"
                              register={register}
                            >
                              <span>Managed Service Screening</span>
                            </FieldRadio>
                          )}
                      </ResponsiveContainer>
                    </FormControl>
                  )}
                  {isManagedDueDiligence && (
                    <FormControl helperText="*Mandatory Fields">
                      <label>Level</label>
                      <ResponsiveContainer className="radio-group">
                        {dueDiligenceLevel &&
                          dueDiligenceLevel.reportTemplates.map((t) => (
                            <FieldRadio
                              key={t.id}
                              label={t.reportName}
                              id={t.id.toString()}
                              value={t.id.toString()}
                              name="reportTemplateId"
                              register={register}
                            >
                              <div>
                                <p>{t.reportName}</p>
                                <p>
                                  {t.fee
                                    ? getSymbolFromCurrency(t.feeCurrencyCode) +
                                      formatCurrencyAmount(t.fee)
                                    : ''}
                                  {t.isFeeRange && t.maxFee && t.fee
                                    ? ' - ' +
                                      getSymbolFromCurrency(t.feeCurrencyCode) +
                                      formatCurrencyAmount(t.maxFee)
                                    : ''}
                                  {t.fee && t.managedServiceDueDiligenceSLA ? ' - ' : ''}
                                  {t.managedServiceDueDiligenceSLA
                                    ? `Up to ${t.managedServiceDueDiligenceSLA} working days`
                                    : ''}
                                </p>
                                {!t.managedServiceDueDiligenceSLA && !t.fee && <br />}
                              </div>
                            </FieldRadio>
                          ))}
                      </ResponsiveContainer>
                    </FormControl>
                  )}
                  <SubmitButtons
                    handleNextSection={handleComplianceCheckTypeNextSectionClick}
                    isLoadingNextButton={
                      relatedThirdPartiesRawDataLoading || potentialDuplicateModalState.loading
                    }
                    showSaveDraftButton={false}
                    disableNextButton={
                      (watch('dueDiligenceType') === 'Screening' &&
                        watch('screeningType') === 'DEFAULT') ||
                      (watch('dueDiligenceType') === 'Due Diligence' &&
                        watch('reportTemplateId') === null) ||
                      watch('dueDiligenceType') === 'DEFAULT' ||
                      relatedThirdPartiesRawDataLoading ||
                      potentialDuplicateModalState.loading
                    }
                  />
                </Accordion>
                {isScreeningDueDiligence && (
                  <Accordion
                    title="Screening settings"
                    isOpen={isOpenSection2}
                    onClick={(event) => {
                      if (completedSections.includes(1)) {
                        event.preventDefault();
                        setIsOpenSection2(!isOpenSection2);
                      }
                    }}
                    completed={completedSections.includes(2)}
                  >
                    <FormControl helperText="*Mandatory Fields">
                      <div className="screening-settings-container">
                        <>
                          <ResponsiveContainer>
                            <FormControl
                              errorMessage={formValidationErrors?.screeningSettings?.message}
                            >
                              <ComboBoxContainer>
                                <FieldSelect
                                  valid={formValidationErrors?.screeningSettings === undefined}
                                  fullWidth
                                  ariaLabel="Initial screening settings *"
                                  id="screeningSettings"
                                  label="Initial screening settings *"
                                  options={initialScreeningSettings}
                                  placeholder="select..."
                                  placeholderValue="DEFAULT"
                                  register={register}
                                />
                              </ComboBoxContainer>
                            </FormControl>
                            {isGlobalSearchEnabled && (
                              <GlobalSearch>
                                <FieldCheckbox
                                  ariaLabel="isGlobalSearch"
                                  id="isGlobalSearch"
                                  name="isGlobalSearch"
                                  label="Perform global search"
                                  register={register}
                                />
                              </GlobalSearch>
                            )}
                          </ResponsiveContainer>
                          {(screeningType === 'Self Service' ||
                            screeningType === 'Managed Service') && (
                            <FormControl errorMessage={formValidationErrors?.monitoring?.message}>
                              <ComboBoxContainer>
                                <FieldSelect
                                  valid={formValidationErrors?.monitoring === undefined}
                                  fullWidth
                                  ariaLabel="Monitoring *"
                                  id="monitoring"
                                  label="Monitoring *"
                                  placeholder="select..."
                                  disabled={
                                    (screeningType === 'Self Service' &&
                                      (isSelfServiceMonitoringEnabledByDefault ||
                                        (!isSelfServiceMonitoringEnabledByDefault &&
                                          !isSelfServiceMonitoringEnabled))) ||
                                    (screeningType === 'Managed Service' &&
                                      (isManagedServiceMonitoringEnabledByDefault ||
                                        (!isManagedServiceMonitoringEnabledByDefault &&
                                          !isManagedServiceMonitoringEnabled)))
                                  }
                                  onChange={() => {}}
                                  placeholderValue="DEFAULT"
                                  options={[
                                    {
                                      name: 'Enabled',
                                      value: 'enabled',
                                    },
                                    {
                                      name: 'Disabled',
                                      value: 'disabled',
                                    },
                                  ]}
                                  register={register}
                                />
                              </ComboBoxContainer>
                            </FormControl>
                          )}
                          {monitoringValue === 'enabled' &&
                            (screeningType === 'Self Service' ||
                              screeningType === 'Managed Service') && (
                              <FormControl
                                errorMessage={
                                  formValidationErrors?.monitoringScreeningSettings?.message
                                }
                              >
                                <FieldSelect
                                  valid={
                                    formValidationErrors?.monitoringScreeningSettings === undefined
                                  }
                                  fullWidth
                                  ariaLabel="Monitoring screening settings *"
                                  id="monitoringScreeningSettings"
                                  label="Monitoring screening settings *"
                                  onChange={() => {}}
                                  options={monitoringScreeningSettings}
                                  placeholder="select..."
                                  placeholderValue="DEFAULT"
                                  disabled={monitoringScreeningSettings.length === 1}
                                  register={register}
                                />
                              </FormControl>
                            )}
                        </>
                        <SubmitButtons
                          handleSubmit={handleScreeningSubmit}
                          showSaveDraftButton={false}
                        />
                      </div>
                    </FormControl>
                  </Accordion>
                )}
                {isManagedDueDiligence && (
                  <>
                    {hasPermittedRelatedThirdParties > 0 && (
                      <Accordion
                        title={`Linked ${terminologyConfig.thirdPartySingularNaming}`}
                        isOpen={isOpenSection2}
                        onClick={(event) => {
                          if (completedSections.includes(1)) {
                            event.preventDefault();
                            setIsOpenSection2(!isOpenSection2);
                          }
                        }}
                        completed={completedSections.includes(2)}
                      >
                        <LinkageThirdPartiesForm
                          mainThirdPartyData={thirdPartyData}
                          individualPermitted={permittedIndividualRelatedThirdParties}
                          companyPermitted={permittedCompanyRelatedThirdParties}
                          linkedThirdParties={formattedLinkedThirdParties}
                          onCreated={prependLinkedThirdParty}
                          onUpdated={updateLinkedThirdParty}
                          linkedThirdPartyConfigurations={linkedThirdPartyConfigurations}
                        />
                        {isAcknowledgeVisible() && (
                          <Panel>
                            <NoteContainer>
                              Note:
                              <AcknowledgeWrapper>
                                You have chosen more than your contractually agreed number of linked{' '}
                                {terminologyConfig.thirdPartyPluralNaming}. This will incur
                                additional fees. A member of Control Risks will contact you to
                                inform you of the total fee of the report before proceeding with the
                                request
                                <FieldRadioContainer>
                                  <FieldCheckbox
                                    ariaLabel="acknowledge"
                                    id="acknowledge"
                                    name="acknowledge"
                                    label="I acknowledge the above and am happy to proceed"
                                    register={register}
                                  />
                                </FieldRadioContainer>
                              </AcknowledgeWrapper>
                            </NoteContainer>
                          </Panel>
                        )}
                        <br />
                        <SubmitButtons
                          handleNextSection={handleRelatedEntitiesNextSectionClick}
                          isLoadingNextButton={potentialDuplicateModalState.loading}
                          disableNextButton={isDisabledRelatedEntitesNextSection}
                        />
                      </Accordion>
                    )}
                    {permittedDocumentsUploadAndPermissions && (
                      <Accordion
                        title={`${terminologyConfig?.thirdPartySingularNaming} documents`}
                        isOpen={isOpenSection3}
                        onClick={(event) => {
                          if (completedSections.includes(2)) {
                            event.preventDefault();
                            setIsOpenSection3(!isOpenSection3);
                          }
                        }}
                        completed={completedSections.includes(3)}
                      >
                        <Controller
                          control={control}
                          name="documentsData"
                          render={({
                            field: { value, onChange },
                            fieldState: { error, invalid },
                          }) => {
                            return (
                              <MultipleDocumentUploadWrapper
                                thirdPartyId={id}
                                thirdPartyData={thirdPartyData}
                                categories={categories}
                                defaultOptionalCategoryId={defaultOptionalCategoryId}
                                documentsConfig={selectedReport.documentConfig}
                                selectedRelatedThirdParties={selectedRelatedThirdParties}
                                value={value}
                                showMandatoryDocuments={showMandatoryDocuments}
                                onChange={onChange}
                                onPendingUploadsChange={handlePendingDocumentUploadsChange}
                                onBlockingUploadsCountChange={
                                  handleBlockingDocumentUploadsCountChange
                                }
                                error={error}
                                isValid={!invalid}
                              />
                            );
                          }}
                        />
                        <SubmitButtons
                          handleNextSection={handleThirdPartyDocumentsNextSectionClick}
                        />
                      </Accordion>
                    )}
                    <Accordion
                      title="Other information"
                      isOpen={isOpenSection4}
                      onClick={(event) => {
                        if (completedSections.includes(3)) {
                          event.preventDefault();
                          setIsOpenSection4(!isOpenSection4);
                        }
                      }}
                      completed={completedSections.includes(4)}
                    >
                      <div className="screening-settings-container">
                        <OtherInformation
                          register={register}
                          dueDiligenceLevel={dueDiligenceLevel}
                          selectedReport={selectedReport}
                          formValidationErrors={formValidationErrors}
                        />
                        {isAcknowledgeMessageVisible && (
                          <Panel>
                            <NoteContainer>
                              Note:
                              <AcknowledgeWrapper>
                                {selectedReport?.acknowledgementMessage}
                                <FieldRadioContainer>
                                  <FieldCheckbox
                                    ariaLabel="hasAcknowledgedMessage"
                                    id="hasAcknowledgedMessage"
                                    name="hasAcknowledgedMessage"
                                    label="I acknowledge the above and am happy to
                            proceed"
                                    register={register}
                                  />
                                </FieldRadioContainer>
                              </AcknowledgeWrapper>
                            </NoteContainer>
                          </Panel>
                        )}
                        {thirdPartyData.isClientCitizenshipByInvestmentEnabled ? (
                          <SubmitButtons handleNextSection={handleOtherInfoNextSectionClick} />
                        ) : (
                          <>
                            {!isValid && (
                              <p style={{ color: 'red' }}>
                                There are some errors that need your attention
                              </p>
                            )}
                            <SubmitButtons handleSubmit={handleDueDiligenceSubmit} />
                          </>
                        )}
                      </div>
                    </Accordion>

                    {thirdPartyData.isClientCitizenshipByInvestmentEnabled && (
                      <Accordion
                        title="CBI interview invitation"
                        isOpen={isOpenSection5}
                        onClick={(event) => {
                          if (completedSections.includes(4)) {
                            event.preventDefault();
                            setIsOpenSection5(!isOpenSection5);
                          }
                        }}
                        completed={completedSections.includes(5)}
                      >
                        <div className="screening-settings-container">
                          <CbiInterviewInvitation
                            register={register}
                            formValidationErrors={formValidationErrors}
                          />
                        </div>

                        {!isValid && (
                          <p style={{ color: 'red' }}>
                            There are some errors that need your attention
                          </p>
                        )}
                        <SubmitButtons handleSubmit={handleDueDiligenceSubmit} />
                      </Accordion>
                    )}
                  </>
                )}
              </DueDiligenceForm>
            )}
          </Wrapper>
          <SideModulesWrapper>
            <ThirdPartyOverviewSideModule
              thirdParty={thirdPartyData}
              thirdPartyDataConfig={thirdPartyDataConfig}
            />
            {isManagedDueDiligence &&
              completedSections.includes(2) &&
              hasPermittedRelatedThirdParties > 0 && (
                <ThirdPartiesSideModule thirdPartiesData={formattedLinkedThirdParties} />
              )}
          </SideModulesWrapper>
        </DueDiligence>
      </FormProvider>

      <PotentialDuplicateModal
        {...potentialDuplicateModalState}
        onProceed={() =>
          setPotentialDuplicateModalState((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
        onClose={() =>
          setPotentialDuplicateModalState((prev) => ({
            ...prev,
            isOpen: false,
          }))
        }
        onCancel={() => history.push(`/third-party/profile/${id}`)}
      />
    </AppLayout>
  );
};
export default InitiateDueDiligenceForm;
